/**
 * The case page's "Source request" view (FB-1007 #8): everything about the
 * NextRequest request a case was created from — number, status, requester,
 * dates, the full request text, and a link to it on NextRequest when the site
 * is known. Fetched live; falls back to what the case stored about it.
 */
import { useEffect, useState } from 'react';
import { ExternalLink, Inbox, Loader2, X } from 'lucide-react';
import { intakeApi } from '../../lib/api';
import type { Case, ExternalRequestDetail, ExternalRequestMeta } from '../../types';
import { providerName, sourceRequestView } from './requestMeta';

interface SourceRequestDialogProps {
  open: boolean;
  onClose: () => void;
  caseData: Case;
  meta: ExternalRequestMeta;
}

export function SourceRequestDialog({ open, onClose, caseData, meta }: SourceRequestDialogProps) {
  const [detail, setDetail] = useState<ExternalRequestDetail | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !meta.external_request_id) return;
    let cancelled = false;
    setLoading(true);
    intakeApi.getExternalRequest(meta.external_request_id, meta.request_number)
      .then((d) => !cancelled && setDetail(d))
      .catch(() => !cancelled && setDetail(null))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [open, meta.external_request_id, meta.request_number]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  const view = sourceRequestView(meta, caseData.name, caseData.notes, detail);
  const source = providerName(detail?.provider || meta.request_provider) ?? 'NextRequest';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-[8vh] z-[100]" role="dialog"
      aria-modal="true" aria-label="Source request">
      <div className="bg-white rounded-lg w-[760px] max-h-[84vh] overflow-hidden flex flex-col shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-2 min-w-0">
            <Inbox className="h-5 w-5 text-primary shrink-0" />
            <div className="min-w-0">
              <p className="text-xs text-gray-500">Source request · {source}</p>
              <h2 className="text-lg font-semibold truncate">{view.title}</h2>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" aria-label="Close">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {loading && !detail ? (
            <p role="status" className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading the request from {source}…
            </p>
          ) : (
            view.stale && (
              <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
                Couldn&apos;t reach {source}; showing what this case saved when it was created.
              </p>
            )
          )}
          <dl className="grid grid-cols-2 gap-x-8 gap-y-1">
            {view.rows.map((row) => (
              <div key={row.label} className="flex justify-between gap-4 border-b border-gray-100 py-1.5 text-sm">
                <dt className="text-gray-500">{row.label}</dt>
                <dd className="font-medium text-gray-800 text-right">{row.value}</dd>
              </div>
            ))}
          </dl>
          <div className="space-y-1">
            <p className="text-xs font-medium text-gray-500">Request text</p>
            {view.text ? (
              <p data-testid="source-request-text"
                className="whitespace-pre-wrap rounded-lg bg-gray-50 p-3 text-sm text-gray-700 max-h-[40vh] overflow-y-auto">
                {view.text}
              </p>
            ) : (
              <p className="text-sm text-gray-400">No request text.</p>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between px-6 py-3 border-t bg-gray-50">
          {view.url ? (
            <a href={view.url} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline">
              Open in {source} <ExternalLink className="h-3.5 w-3.5" />
            </a>
          ) : <span />}
          <button onClick={onClose} className="rounded-md border border-gray-300 px-3 py-1.5 text-sm hover:bg-white">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
